"use client";

import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useDebounced } from "@/hooks/use-debounced";
import { SearchIcon } from "./library-icons";

/**
 * Le champ de recherche de la bibliothèque, et sa synchronisation avec l'URL.
 *
 * Le catalogue, « Mes morceaux » et « Partagé avec moi » le portent. Un seul
 * composant pour que les trois attendent le même temps mort et aient la même
 * forme : on cherche au même endroit, du même geste, où qu'on soit.
 */

/** Temps mort avant qu'une frappe atteigne l'URL. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * La valeur du champ, locale, et son écriture dans l'URL après un temps mort.
 *
 * L'URL reste la source de vérité de ce qui est *interrogé* ; le champ, lui,
 * doit suivre la frappe sans attendre l'aller-retour.
 *
 * @param query la recherche lue dans l'URL, déjà nettoyée
 * @param write écrit une recherche dans l'URL (un `router.replace`)
 */
export function useSearchDraft(
  query: string,
  write: (next: string) => void,
): [string, (next: string) => void] {
  const [draft, setDraft] = useState(query);
  const debounced = useDebounced(draft, SEARCH_DEBOUNCE_MS);

  /**
   * Dernière valeur que *nous* avons écrite dans l'URL.
   *
   * C'est l'arbitre entre les deux effets ci-dessous, qui se pousseraient
   * sinon en boucle : on écrit `q=cho`, l'URL change, le second effet voit une
   * URL différente et réécrit le champ — en écrasant les lettres tapées
   * pendant l'aller-retour. En comparant à ce qu'on a poussé, on ne réadopte
   * l'URL que lorsqu'elle vient d'ailleurs : un retour navigateur, un lien
   * partagé, un clic dans la sidebar.
   */
  const pushed = useRef(query);

  // `write` est recréé à chaque rendu par l'appelant : en événement d'effet,
  // il ne relance pas l'effet de frappe à chaque rendu.
  const writeQuery = useEffectEvent(write);

  // ── La frappe écrit l'URL, après le temps mort ────────────────
  useEffect(() => {
    // Le temps mort n'a pas rattrapé la frappe — ou une navigation vient de
    // réinitialiser le champ, auquel cas `debounced` porte encore l'ancienne
    // recherche et la repousserait par-dessus la destination qu'on vient
    // d'ouvrir. Dans les deux cas il n'y a rien à écrire.
    if (debounced !== draft) {
      return;
    }

    const next = debounced.trim();

    if (next === pushed.current) {
      return;
    }

    pushed.current = next;
    writeQuery(next);
  }, [debounced, draft]);

  // ── Une URL venue d'ailleurs réécrit le champ ─────────────────
  useEffect(() => {
    if (query === pushed.current) {
      return;
    }

    pushed.current = query;
    setDraft(query);
  }, [query]);

  return [draft, setDraft];
}

export function LibrarySearchField({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  /** Nom accessible : dit *où* l'on cherche, le texte d'appel ne le dit pas. */
  label: string;
}) {
  return (
    <div className="relative w-full max-w-[520px]">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none">
        <SearchIcon />
      </span>
      {/*
        Pas d'`autoFocus` : sur un téléphone il lève le clavier virtuel dès
        l'arrivée, ce qui masque la moitié de l'écran qu'on vient d'ouvrir
        pour le regarder. Chercher est une intention, pas un état par défaut.
      */}
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={label}
        className="w-full h-11 rounded-xl pl-10 pr-4 text-[14px] text-text-primary placeholder:text-text-muted
          bg-white/[0.04] border border-border-subtle transition-colors duration-150
          focus:border-accent/40 focus:outline-none focus:ring-1 focus:ring-accent/15"
      />
    </div>
  );
}
